import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    TablePagination,
    Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAPI } from "../../../network";
import { tableHeader } from "../../../utils";
import { useNavigate } from 'react-router-dom';
import { Tooltip } from "@mui/material";

// Color function
const getScoreColor = (score) => {
    if (score >= 9) return "#399914";
    if (score >= 7) return "#66FF66";
    if (score >= 4) return "#FFA500";
    return "#FF3333";
};

// CSV export utility
const exportToCSV = (data, headers, fileName) => {
    const escape = (val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`;

    const csvContent = [
        headers.join(","),
        ...data.map((row) =>
            headers.map((field) => escape(row[field])).join(",")
        ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const ResponsesPage = () => {
    const navigate = useNavigate();
    const [allResponses, setAllResponses] = useState({});
    const [filteredQuestionnaire, setFilteredQuestionnaire] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [pagination, setPagination] = useState({}); // { [questionnaireId]: { page, rowsPerPage } }
    let companyId = localStorage.getItem('companyIdLS')
    if (companyId === null) {
        companyId = undefined
    }
    useEffect(() => {
        const fetchData = async () => {
            const response = await getAPI(`/building/survey-summary?companyId=${companyId}`);
            if (response) {
                const grouped = response.reduce((acc, item) => {
                    const id = item.questionnaireId;
                    if (!acc[id]) acc[id] = { title: item.questionnaireTitle, data: [] };
                    acc[id].data.push(item);
                    return acc;
                }, {});
                setAllResponses(grouped);
                const initial = {};
                for (const key of Object.keys(grouped)) {
                    initial[key] = { page: 0, rowsPerPage: 5 };
                }
                setPagination(initial);
            }
        };
        fetchData();
    }, []);

    const questionnaireOptions = Object.entries(allResponses).map(([id, group]) => ({
        id,
        title: group.title
    }));

    const isInDateRange = (dateStr) => {
        const date = new Date(dateStr);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start && date < start) return false;
        if (end && date > end) return false;
        return true;
    };

    const shownQuestionnaires = filteredQuestionnaire === "all"
        ? Object.entries(allResponses)
        : Object.entries(allResponses).filter(([id]) => id === filteredQuestionnaire);

    return (
        <Box p={3}>
            <Typography variant="h5" mb={2}>Survey Responses</Typography>

            {/* Filters */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                    label="Questionnaire"
                    select
                    value={filteredQuestionnaire}
                    onChange={(e) => setFilteredQuestionnaire(e.target.value)}
                    sx={{ minWidth: 250 }}
                >
                    <MenuItem value="all">All Questionnaires</MenuItem>
                    {questionnaireOptions.map((q) => (
                        <MenuItem key={q.id} value={q.id}>{q.title}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {shownQuestionnaires.map(([questionnaireId, group]) => {
                const filteredData = group.data.filter((res) => isInDateRange(res.date));
                const questionLabels = Object.keys(filteredData[0] || {}).filter(
                    key => !["questionnaireId", "questionnaireTitle", "date", "fsrId", "siteName", "faultType", "notes"].includes(key)
                );

                const { page, rowsPerPage } = pagination[questionnaireId] || { page: 0, rowsPerPage: 5 };
                const paginated = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

                return (
                    <Accordion key={questionnaireId} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight="bold">
                                {group.title} ({filteredData.length} responses)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>

                            {/* Export Button */}
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        exportToCSV(
                                            filteredData,
                                            ["date", "fsrId", "siteName", "faultType", ...questionLabels, "notes"],
                                            `${group.title.replace(/\s+/g, "_")}_${startDate || "start"}-${endDate || "end"}`
                                        )
                                    }
                                >
                                    Export CSV
                                </Button>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead sx={tableHeader}>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>FSR ID</TableCell>
                                            <TableCell>Site Name</TableCell>
                                            <TableCell>Fault Type</TableCell>
                                            {questionLabels.map((q, idx) => (
                                                <TableCell key={idx}>
                                                    <Tooltip title={q} arrow>
                                                        <Box
                                                            sx={{
                                                                maxWidth: 150,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {q}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            ))}
                                            <TableCell>Notes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginated.map((res, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{res.date}</TableCell>
                                                <TableCell
                                                    onClick={() => navigate(`/fsr/facilities?fsrId=${res.fsrId}`)}
                                                    sx={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                                                >
                                                    {res.fsrId}
                                                </TableCell>

                                                <TableCell>{res.siteName}</TableCell>
                                                <TableCell>{res.faultType}</TableCell>
                                                {questionLabels.map((q, idx) => (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            backgroundColor: getScoreColor(res[q]),
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        {res[q]}
                                                    </TableCell>
                                                ))}
                                                <TableCell>
                                                    <Tooltip title={res.notes || ""} arrow>
                                                        <Box
                                                            sx={{
                                                                maxWidth: 200,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {res.notes}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={filteredData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(_, newPage) =>
                                        setPagination((prev) => ({
                                            ...prev,
                                            [questionnaireId]: { ...prev[questionnaireId], page: newPage },
                                        }))
                                    }
                                    onRowsPerPageChange={(e) =>
                                        setPagination((prev) => ({
                                            ...prev,
                                            [questionnaireId]: {
                                                page: 0,
                                                rowsPerPage: parseInt(e.target.value, 10),
                                            },
                                        }))
                                    }
                                />
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default ResponsesPage;
